import './shell/shell.css';
import './modules/modules.css';
import './modules/lesson.css';
import './assets/assets.css';
import './sounds/sounds.css';
import { findPrototype } from './prototypes/registry';
import { findModule } from './modules/content/binatang-peliharaan';
import { renderGallery } from './modules/gallery';
import { renderPlayer } from './modules/runtime/player';
import type { PrototypeHandle } from './prototypes/types';
import { renderMenu } from './shell/menu';
import { readPackStatus, registerServiceWorker } from './shell/pwa';
import { createStatsOverlay, type StatsOverlay } from './shell/stats';
import {
  DEFAULT_RESOLUTION,
  RESOLUTION_MODES,
  isResolutionMode,
  type ResolutionMode,
} from './engine/resolution';

const rootElement = document.querySelector<HTMLElement>('#app');
if (!rootElement) throw new Error('#app host element is missing from index.html');
const root: HTMLElement = rootElement;

let current: PrototypeHandle | null = null;
let stats: StatsOverlay | null = null;
/** Cancels any listeners registered by the mounted prototype's stage. */
let stageListeners: AbortController | null = null;
/**
 * Tears down whichever non-prototype route is mounted - the prototype menu, the
 * lesson gallery, or a module player. Prototypes clean up through `current`
 * instead, since they own GPU resources the shell knows nothing about.
 */
let disposeRoute: (() => void) | null = null;

/** Guards against a second navigation landing while an async mount is in flight. */
let navToken = 0;

function teardown(): void {
  disposeRoute?.();
  disposeRoute = null;
  stageListeners?.abort();
  stageListeners = null;
  current?.unmount();
  current = null;
  stats?.destroy();
  stats = null;
  root.innerHTML = '';
}

/**
 * @param stored whether this pack was recorded as saved for offline use.
 *
 * Deliberately keyed on that rather than on `navigator.onLine`, which only
 * reports whether the device has a network interface - not whether anything is
 * reachable through it. A school with Wi-Fi but no working uplink reports
 * `onLine === true`, and would have been told to check a connection it already
 * has. Whether we stored the pack is a fact we actually know.
 */
function renderUnavailable(surface: HTMLElement, stored: boolean): void {
  const notice = document.createElement('div');
  notice.className = 'stage__unavailable';
  notice.innerHTML = `
    <h2></h2>
    <p></p>
    <a href="#/">← Kembali ke daftar</a>
  `;

  (notice.querySelector('h2') as HTMLElement).textContent = stored
    ? 'Gagal memuat paket'
    : 'Paket ini belum diunduh';

  (notice.querySelector('p') as HTMLElement).textContent = stored
    ? 'Paket sudah tersimpan tetapi gagal dibuka. Coba muat ulang halaman.'
    : 'Paket pelajaran ini belum tersimpan di perangkat dan tidak dapat diambil sekarang. Sambungkan ke internet, lalu unduh dari daftar "Tersedia offline".';

  surface.appendChild(notice);
}

/** Path segments after the hash, e.g. `#/belajar/rpph-1-kucing` -> two. */
function routeSegments(): string[] {
  return window.location.hash
    .replace(/^#\/?/, '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

/**
 * The lesson lane: a gallery of modules, and one module player.
 *
 * Mounted from the shell rather than given its own entry point so that both
 * lanes share one service worker, one install, and one offline story. A teacher
 * and a stakeholder are looking at the same deployment.
 */
async function mountLesson(segments: string[], token: number): Promise<void> {
  const moduleId = segments[1];

  if (!moduleId) {
    disposeRoute = renderGallery(root);
    return;
  }

  const found = findModule(moduleId);
  if (!found || found.module.status !== 'ready') {
    window.location.hash = '#/belajar';
    return;
  }

  const dispose = await renderPlayer(root, found.subtema, found.module);

  // A newer navigation landed while the module's chunk was loading; the player
  // has already written into `root`, so tear it down rather than leaving two
  // routes layered on top of each other.
  if (token !== navToken) {
    dispose();
    return;
  }

  disposeRoute = dispose;
}

async function mountPrototype(id: string, token: number): Promise<void> {
  const prototype = findPrototype(id);

  if (!prototype?.load) {
    window.location.hash = '';
    return;
  }

  const stage = document.createElement('div');
  stage.className = 'stage';

  const bar = document.createElement('div');
  bar.className = 'stage__bar';
  bar.innerHTML = `
    <a class="stage__back" href="#/">← Prototypes</a>
    <span class="stage__title"></span>
  `;
  bar.querySelector('.stage__title')!.textContent = prototype.title;

  const surface = document.createElement('div');
  surface.className = 'stage__surface';

  stage.append(bar, surface);
  root.append(stage);

  let handle: PrototypeHandle;
  try {
    const module = await prototype.load();
    handle = await module.mount(surface);
  } catch {
    // A blank screen is the worst possible answer here - section 6.1 is about
    // the offline model being understandable, not merely functional.
    const state = await readPackStatus(prototype);
    renderUnavailable(surface, state.status === 'ready');
    return;
  }

  // A newer navigation started while this one was loading - discard this mount.
  if (token !== navToken) {
    handle.unmount();
    return;
  }

  current = handle;
  stageListeners = new AbortController();

  stats = createStatsOverlay();
  bar.append(stats.element);

  const reportBackbuffer = () => {
    const size = handle.backbufferSize?.();
    stats?.setDetail(size ? `${size.width} × ${size.height} px` : '');
  };

  if (handle.setResolution) {
    const select = document.createElement('select');
    select.className = 'stage__resolution';
    select.setAttribute('aria-label', 'Render resolution');
    select.innerHTML = RESOLUTION_MODES.map(
      (m) => `<option value="${m}"${m === DEFAULT_RESOLUTION ? ' selected' : ''}>${m}</option>`,
    ).join('');

    select.addEventListener(
      'change',
      () => {
        const mode: ResolutionMode = isResolutionMode(select.value)
          ? select.value
          : DEFAULT_RESOLUTION;
        handle.setResolution!(mode);
        // Start a clean measurement window, otherwise the average still carries
        // frames rendered at the previous resolution.
        stats?.reset();
        requestAnimationFrame(reportBackbuffer);
      },
      { signal: stageListeners.signal },
    );

    bar.insertBefore(select, stats.element);
  }

  requestAnimationFrame(reportBackbuffer);
  window.addEventListener('resize', reportBackbuffer, { signal: stageListeners.signal });
}

async function route(): Promise<void> {
  const token = ++navToken;
  teardown();

  const segments = routeSegments();

  if (segments.length === 0) {
    disposeRoute = renderMenu(root);
    return;
  }

  if (segments[0] === 'belajar') {
    await mountLesson(segments, token);
    return;
  }

  // Lazy: the asset workspace is for collaborators, and a teacher opening a
  // lesson should never download it. It also parses every drawing in the repo,
  // which is work worth doing only when somebody asks for it.
  if (segments[0] === 'aset') {
    // Asset ids contain a slash (`kucing/utuh`), so everything after `aset` is
    // the id rather than a second route segment.
    const id = segments.slice(1).join('/');

    if (id) {
      const { renderAssetDetail } = await import('./assets/detail');
      if (token !== navToken) return;
      disposeRoute = renderAssetDetail(root, id);
      return;
    }

    const { renderWorkspace } = await import('./assets/workspace');
    if (token !== navToken) return;
    disposeRoute = renderWorkspace(root);
    return;
  }

  // Lazy for the same reason as `aset`: a recording checklist is for whoever is
  // producing the audio, and a teacher opening a lesson should never fetch it.
  if (segments[0] === 'suara') {
    const { renderSoundChecklist } = await import('./sounds/checklist');
    if (token !== navToken) return;
    disposeRoute = renderSoundChecklist(root);
    return;
  }

  await mountPrototype(segments[0]!, token);
}

window.addEventListener('hashchange', () => {
  void route();
});

// Registration is a no-op in dev and on browsers without support; offline is a
// production-build behaviour, tested with `pnpm build && pnpm preview`.
void registerServiceWorker();

void route();
